import React, { createContext, useState, useContext, useEffect, useMemo } from 'react';
import * as adminService from '../lib/services/adminService';

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [activeOffers, setActiveOffers] = useState([]);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('munchups_cart');
    if (savedCart) {
      try {
        setCartItems(JSON.parse(savedCart));
      } catch (error) {
        console.error('Error loading cart:', error);
      }
    }
  }, []);

  
  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('munchups_cart', JSON.stringify(cartItems));
  }, [cartItems]);

  useEffect(() => {
    const loadOffers = async () => {
      try {
        const offers = await adminService.getPublicOffers();
        setActiveOffers(Array.isArray(offers) ? offers : []);
      } catch (error) {
        console.error('Error loading offers for cart pricing:', error);
        setActiveOffers([]);
      }
    };

    loadOffers();
  }, []);

  const pricing = useMemo(() => {
    const normalizedItems = cartItems.map((item) => {
      const baseUnitPrice = item.discounted_price && item.discounted_price < item.price
        ? Number(item.discounted_price)
        : Number(item.price || 0);
      const quantity = Math.max(0, parseInt(item.quantity, 10) || 0);
      return {
        ...item,
        base_unit_price: baseUnitPrice,
        quantity,
      };
    });

    const linePriceMap = new Map();
    normalizedItems.forEach((item) => {
      linePriceMap.set(item.id, {
        unit_price_before_offer: item.base_unit_price,
        unit_price_after_offer: item.base_unit_price,
        line_total_before_offer: item.base_unit_price * item.quantity,
        line_total_after_offer: item.base_unit_price * item.quantity,
        bundled_quantity: 0,
        bundled_line_total: 0,
      });
    });

    const offersByCategory = new Map();
    (activeOffers || []).forEach((offer) => {
      const categoryKey = String(offer.category || '').trim().toLowerCase();
      const offerType = String(offer.offer_type || 'bundle').trim().toLowerCase();
      const requiredQuantity = parseInt(offer.required_quantity, 10);
      const offerPrice = Number(offer.offer_price || 0);
      const discountPercent = Number(offer.discount_percent || 0);

      if (!categoryKey || !['bundle', 'discount'].includes(offerType)) {
        return;
      }
      if (offerType === 'bundle' && (!Number.isInteger(requiredQuantity) || requiredQuantity < 2 || offerPrice <= 0)) {
        return;
      }
      if (offerType === 'discount' && (discountPercent <= 0 || discountPercent > 100)) {
        return;
      }
      if (!offersByCategory.has(categoryKey)) {
        offersByCategory.set(categoryKey, []);
      }
      offersByCategory.get(categoryKey).push({
        ...offer,
        offer_type: offerType,
        required_quantity: requiredQuantity,
        offer_price: offerPrice,
        discount_percent: discountPercent,
      });
    });

    const offerApplications = [];

    offersByCategory.forEach((categoryOffers, categoryKey) => {
      const categoryItems = normalizedItems.filter((item) => String(item.category || '').trim().toLowerCase() === categoryKey);
      if (categoryItems.length === 0) return;

      const units = [];
      categoryItems.forEach((item) => {
        for (let i = 0; i < item.quantity; i++) {
          units.push({
            itemId: item.id,
            basePrice: Number(item.base_unit_price || 0),
          });
        }
      });

      if (units.length === 0) return;

      // Sort ascending so the bundle is applied to the lowest-priced items first;
      // if the cart has more items than the bundle requires, the cheapest ones
      // are bundled and the more expensive items pay their full individual price.
      units.sort((a, b) => a.basePrice - b.basePrice);

      while (units.length > 0) {
        let bestOffer = null;
        let bestSavings = 0;

        for (const offer of categoryOffers) {
          let candidateUnits = [];
          let candidateTotal = 0;
          let targetTotal = 0;

          if (offer.offer_type === 'bundle') {
            if (offer.required_quantity > units.length) continue;
            candidateUnits = units.slice(0, offer.required_quantity);
            candidateTotal = candidateUnits.reduce((sum, unit) => sum + unit.basePrice, 0);
            targetTotal = offer.offer_price;
          } else {
            candidateUnits = units.slice();
            candidateTotal = candidateUnits.reduce((sum, unit) => sum + unit.basePrice, 0);
            targetTotal = candidateTotal * (1 - (offer.discount_percent / 100));
          }

          const savings = candidateTotal - targetTotal;
          if (savings > bestSavings) {
            bestSavings = savings;
            bestOffer = {
              offer,
              candidateUnits,
              candidateTotal,
              targetTotal,
            };
          }
        }

        if (!bestOffer || bestSavings <= 0) {
          break;
        }

        const ratio = bestOffer.candidateTotal > 0
          ? (bestOffer.targetTotal / bestOffer.candidateTotal)
          : 1;

        bestOffer.candidateUnits.forEach((unit) => {
          const line = linePriceMap.get(unit.itemId);
          if (!line) return;
          const adjustedPrice = unit.basePrice * ratio;
          line.line_total_after_offer -= unit.basePrice;
          line.line_total_after_offer += adjustedPrice;
          if (bestOffer.offer.offer_type === 'bundle') {
            line.bundled_quantity += 1;
            line.bundled_line_total += adjustedPrice;
          }
        });

        const existingApplication = offerApplications.find((entry) => entry.offer_id === bestOffer.offer.id);
        if (existingApplication) {
          existingApplication.application_count += 1;
          existingApplication.total_savings += bestSavings;
        } else {
          offerApplications.push({
            offer_id: bestOffer.offer.id,
            offer_type: bestOffer.offer.offer_type,
            title: bestOffer.offer.title || null,
            category: bestOffer.offer.category,
            required_quantity: bestOffer.offer.required_quantity,
            offer_price: bestOffer.offer.offer_price,
            discount_percent: bestOffer.offer.discount_percent,
            currency: bestOffer.offer.currency || 'SEK',
            application_count: 1,
            total_savings: bestSavings,
          });
        }

        if (bestOffer.offer.offer_type === 'bundle') {
          units.splice(0, bestOffer.offer.required_quantity);
        } else {
          units.length = 0;
        }
      }
    });

    let subtotalBeforeOffers = 0;
    let subtotalAfterOffers = 0;
    let totalVAT = 0;

    const pricedItems = normalizedItems.map((item) => {
      const line = linePriceMap.get(item.id);
      const lineBefore = Number(line?.line_total_before_offer || 0);
      const lineAfter = Number(line?.line_total_after_offer || lineBefore);
      const adjustedUnitPrice = item.quantity > 0 ? lineAfter / item.quantity : Number(item.base_unit_price || 0);
      const vatPercent = Number(item.vat_percent || 0);
      const vatAmount = lineAfter * (vatPercent / (100 + vatPercent));

      subtotalBeforeOffers += lineBefore;
      subtotalAfterOffers += lineAfter;
      totalVAT += vatAmount;

      const bundledQty = line?.bundled_quantity ?? 0;
      const bundledLineTotal = Number(line?.bundled_line_total ?? 0);
      const unbundledQty = item.quantity - bundledQty;

      return {
        ...item,
        adjusted_unit_price: adjustedUnitPrice,
        line_total_before_offer: lineBefore,
        line_total_after_offer: lineAfter,
        bundled_quantity: bundledQty,
        bundled_line_total: bundledLineTotal,
        unbundled_quantity: unbundledQty,
        unbundled_unit_price: Number(item.base_unit_price || 0),
      };
    });

    return {
      pricedItems,
      subtotalBeforeOffers,
      subtotalAfterOffers,
      offerDiscount: Math.max(0, subtotalBeforeOffers - subtotalAfterOffers),
      offerApplications,
      totalVAT,
    };
  }, [cartItems, activeOffers]);

  const addToCart = (product, quantity = 1) => {
    setCartItems((prevItems) => {
      const existingItem = prevItems.find((item) => item.id === product.id);
      
      if (existingItem) {
        // Update quantity if product already in cart
        return prevItems.map((item) =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      } else {
        // Add new product to cart
        return [...prevItems, { ...product, quantity }];
      }
    });
  };

  const removeFromCart = (productId) => {
    setCartItems((prevItems) =>
      prevItems.filter((item) => item.id !== productId)
    );
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
    } else {
      setCartItems((prevItems) =>
        prevItems.map((item) =>
          item.id === productId ? { ...item, quantity } : item
        )
      );
    }
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getTotalPrice = () => {
    return pricing.subtotalAfterOffers;
  };

  const getTotalItems = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  const getTotalVAT = () => {
    return pricing.totalVAT;
  };

  const getCartPricing = () => pricing;

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getTotalPrice,
        getTotalItems,
        getTotalVAT,
        getCartPricing,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
